// src/pages/MyBookingsPage.jsx

import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Container,
  Typography,
  Alert,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Button,
  TextField,
  Tabs,
  Tab,
  Chip
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import moment from "moment-timezone";
import { isFutureBookingInOrgTimezone } from "../utils/timezoneUtils";
import { useSnackbar } from "notistack";
import { getMyBookings, cancelMyBooking, updateBooking } from "../services/bookingService";
import { useTimezone } from "../contexts/TimezoneContext";
import EditBookingModal from "../components/EditBookingModal";
import BookingCard from "../components/BookingCard";
import BookingCardSkeleton from "../components/BookingCardSkeleton";

const ITEMS_PER_PAGE = 12;

export default function MyBookingsPage() {
  // short English comments only

  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState("");
  const { orgTimeZone } = useTimezone();

  // filters
  const [selectedRoom, setSelectedRoom] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);

  // tab state: "future" or "past"
  const [tabValue, setTabValue] = useState("future");

  // dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);

  // edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // infinite scroll state
  const [displayedBookings, setDisplayedBookings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const { enqueueSnackbar } = useSnackbar();

  // Intersection observer ref for infinite scroll
  const observerTarget = useRef(null);

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line
  }, []);

  const loadMoreBookings = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    
    // Simulate network delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setCurrentPage(prev => prev + 1);
    setIsLoadingMore(false);
  }, [isLoadingMore, hasMore]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isInitialLoading) {
          loadMoreBookings();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoadingMore, isInitialLoading, loadMoreBookings]);

  const updateDisplayedBookings = useCallback(() => {
    const itemsToShow = currentPage * ITEMS_PER_PAGE;
    const newDisplayedBookings = bookings.slice(0, itemsToShow);
    setDisplayedBookings(newDisplayedBookings);
    setHasMore(bookings.length > itemsToShow);
  }, [currentPage, bookings]);

  // Update displayed bookings when bookings or pagination changes
  useEffect(() => {
    updateDisplayedBookings();
  }, [updateDisplayedBookings]);

  async function fetchBookings() {
    try {
      setError("");
      setIsInitialLoading(true);
      const data = await getMyBookings();
      setBookings(data);
      setCurrentPage(1);
      setHasMore(data.length > ITEMS_PER_PAGE);
    } catch (err) {
      setError("Failed to load your bookings");
      enqueueSnackbar("Failed to load bookings", { variant: "error" });
    } finally {
      setIsInitialLoading(false);
    }
  }

  function handleOpenDialog(bookingId) {
    setBookingToCancel(bookingId);
    setDialogOpen(true);
  }

  function handleCloseDialog() {
    setBookingToCancel(null);
    setDialogOpen(false);
  }

  async function handleConfirmCancel() {
    if (!bookingToCancel) return;
    try {
      await cancelMyBooking(bookingToCancel);
      setBookings((prev) => prev.filter((b) => b._id !== bookingToCancel));
      enqueueSnackbar("Booking canceled successfully", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Failed to cancel booking", { variant: "error" });
    } finally {
      setDialogOpen(false);
      setBookingToCancel(null);
    }
  }

  // edit booking functions
  function handleOpenEdit(booking) {
    setSelectedBooking(booking);
    setEditModalOpen(true);
  }

  function handleCloseEdit() {
    setSelectedBooking(null);
    setEditModalOpen(false);
  }

  async function handleSaveEdit(bookingId, updateData) {
    try {
      const response = await updateBooking(bookingId, updateData);
      // Update the booking in the state - response contains {message, booking}
      setBookings((prev) => 
        prev.map((b) => 
          b._id === bookingId ? { ...b, ...response.booking } : b
        )
      );
      enqueueSnackbar("Booking updated successfully", { variant: "success" });
    } catch (error) {
      console.error("Error updating booking:", error);
      throw error; // Re-throw to let the modal handle the error display
    }
  }

  // checks if the booking endTime is in the future (in organization timezone)
  function isFutureBooking(b) {
    return isFutureBookingInOrgTimezone(b.endTime, orgTimeZone);
  }

  // separate future vs past
  const future = bookings.filter(isFutureBooking);
  const past = bookings.filter((b) => !isFutureBooking(b));

  // filters for the selected list
  function filterBookings(list) {
    let result = [...list];
    // room filter
    if (selectedRoom !== "all") {
      result = result.filter((b) => b.room && b.room._id === selectedRoom);
    }
    // date filter (using organization timezone)
    if (selectedDate) {
      const daySelected = moment.tz(selectedDate.toDate(), orgTimeZone).startOf("day");
      result = result.filter((b) => {
        const start = moment.tz(b.startTime, orgTimeZone).startOf("day");
        return start.isSame(daySelected, "day");
      });
    }
    return result;
  }

  // gather unique rooms
  const uniqueRooms = [];
  bookings.forEach((b) => {
    if (b.room && !uniqueRooms.find((r) => r._id === b.room._id)) {
      uniqueRooms.push(b.room);
    }
  });

  // pick the relevant data list by tab
  const displayList = tabValue === "future" ? future : past;
  const filtered = filterBookings(displayList);
  
  // Apply pagination to filtered results
  const filteredDisplayedBookings = filterBookings(displayedBookings.filter(b => 
    tabValue === "future" ? isFutureBooking(b) : !isFutureBooking(b)
  ));

  // Check if we should show skeleton (only when initially loading or when we have some results)
  const shouldShowSkeleton = isInitialLoading || (isLoadingMore && displayedBookings.length > 0);
  const skeletonsCount = isInitialLoading ? 6 : 3; // Show more skeletons on initial load

  function handleTabChange(e, newValue) {
    setTabValue(newValue);
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Typography variant="h4">
          My Bookings
        </Typography>
        <Chip 
          label={`Times shown in ${orgTimeZone}`} 
          size="small" 
          variant="outlined" 
          color="info"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs for future / past */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Future Bookings" value="future" />
          <Tab label="Past Bookings" value="past" />
        </Tabs>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="filter-room-label">Filter by Room</InputLabel>
            <Select
              labelId="filter-room-label"
              label="Filter by Room"
              value={selectedRoom}
              onChange={(e) => setSelectedRoom(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {uniqueRooms.map((room) => (
                <MenuItem key={room._id} value={room._id}>
                  {room.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Filter by Date"
              value={selectedDate}
              onChange={(newVal) => setSelectedDate(newVal)}
              slots={{
                textField: TextField
              }}
              slotProps={{
                textField: {
                  sx: { minWidth: 220 }
                }
              }}
            />
          </LocalizationProvider>

          <Button
            variant="outlined"
            onClick={() => {
              setSelectedRoom("all");
              setSelectedDate(null);
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {filtered.length === 0 && !isInitialLoading ? (
        <Typography>No bookings found.</Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {/* Render actual booking cards */}
            {filteredDisplayedBookings.map((b) => {
              // checks if it's future or ended, to show or hide "Cancel" and "Edit"
              const futureBooking = isFutureBooking(b);
              return (
                <Grid item xs={12} md={6} lg={4} key={b._id}>
                  <BookingCard
                    booking={b}
                    orgTimeZone={orgTimeZone}
                    onCancel={futureBooking && tabValue === "future" ? handleOpenDialog : null}
                    showEdit={futureBooking && tabValue === "future"}
                    onEdit={handleOpenEdit}
                  />
                </Grid>
              );
            })}
            
            {/* Render skeleton cards during loading */}
            {shouldShowSkeleton && Array.from({ length: skeletonsCount }).map((_, index) => (
              <Grid item xs={12} md={6} lg={4} key={`skeleton-${index}`}>
                <BookingCardSkeleton />
              </Grid>
            ))}
          </Grid>
          
          {/* Infinite scroll trigger point */}
          {hasMore && !isInitialLoading && (
            <Box 
              ref={observerTarget} 
              sx={{ 
                height: 20, 
                mt: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {isLoadingMore && (
                <BookingCardSkeleton />
              )}
            </Box>
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this booking?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            No
          </Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Booking Modal */}
      <EditBookingModal
        open={editModalOpen}
        onClose={handleCloseEdit}
        booking={selectedBooking}
        onSave={handleSaveEdit}
        organizationWorkHours={{ start: "08:00", end: "18:00" }} // You can make this dynamic if needed
      />
    </Container>
  );
}